/**
 * LearningJourneyView.js — Best-in-Class Learning Tracker
 *
 * Full-page view (not a dialog) with 4 tabs:
 *   1. Overview   — heatmap, top hubs, recent activity feed
 *   2. Activity Log — date-grouped event timeline with filters
 *   3. Analytics  — Bloom's radar, mode doughnut, study bar, hour heat strip
 *   4. Insights   — AI-generated strengths, improvements, habits
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Chip, IconButton,
  CircularProgress, Skeleton, Tooltip, Button, Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Hub as HubIcon,
  PictureAsPdf as PdfIcon,
  AutoAwesome as AIIcon,
  Science as ToolIcon,
  EmojiEvents as TrophyIcon,
  LocalFireDepartment as FireIcon,
  QueryStats as AnalyticsIcon,
  Lightbulb as InsightIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  Psychology as BrainIcon,
  FactCheck as QuizResultsIcon,
  CheckCircle as CorrectIcon,
  CalendarMonth as PlanIcon,
  Assessment as ReportIcon,
  Replay as ReaskIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenHubIcon,
  CheckCircleOutline as CompletedPlanIcon,
  RadioButtonUnchecked as InProgressPlanIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Radar, Doughnut, Bar } from 'react-chartjs-2';
import {
  getJourneyEvents,
  getDailyActivity,
  getTopHubs,
  getHourDistribution,
  analyzeBloomsLevel,
  generateInsights,
  logJourneyEvent,
} from '../services/learningJourneyService';
import { getSessionHistory } from '../services/sessionHistoryService';
import { getAllStudyPlans } from '../services/studyPlanCacheService';
import StudentReportView from './StudentReportView';

// Register Chart.js components
ChartJS.register(
  RadialLinearScale, PointElement, LineElement, Filler,
  ArcElement, CategoryScale, LinearScale, BarElement,
  ChartTooltip, Legend
);

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatRelativeTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatTime = (date) =>
  date instanceof Date
    ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : '';

const formatDateLabel = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const EVENT_META = {
  hub_visit: { icon: <HubIcon sx={{ fontSize: 16 }} />, color: '#3b82f6', label: 'Hub' },
  pdf_open:  { icon: <PdfIcon sx={{ fontSize: 16 }} />,  color: '#ef4444', label: 'PDF' },
  ai_query:  { icon: <AIIcon sx={{ fontSize: 16 }} />,   color: '#8b5cf6', label: 'AI' },
  tool_open: { icon: <ToolIcon sx={{ fontSize: 16 }} />, color: '#10b981', label: 'Tool' },
};

const eventDescription = (ev) => {
  switch (ev.type) {
    case 'hub_visit': return `Opened Hub: ${ev.hubName || '—'}`;
    case 'pdf_open':  return `Opened PDF: ${ev.pdfName || '—'}`;
    case 'ai_query':  return `[${ev.mode || 'Query'}] ${ev.queryText?.slice(0, 60) || ''}${(ev.queryText?.length > 60) ? '…' : ''}`;
    case 'tool_open': return `Used ${ev.toolName || 'Lab'}`;
    default: return ev.type;
  }
};

const BLOOMS_COLORS = {
  Remember:   'rgba(33,150,243,0.7)',
  Understand: 'rgba(0,188,212,0.7)',
  Apply:      'rgba(76,175,80,0.7)',
  Analyze:    'rgba(255,152,0,0.7)',
  Evaluate:   'rgba(244,67,54,0.7)',
  Create:     'rgba(156,39,176,0.7)',
};

const BLOOMS_LEVELS = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Heatmap ───────────────────────────────────────────────────────────────────

function ActivityHeatmap({ dailyActivity, days }) {
  // Build ordered date array (oldest → newest, padded to full weeks)
  const dates = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dates.push(d.toISOString().slice(0, 10));
  }

  const maxCount = Math.max(...dates.map(d => dailyActivity[d]?.count || 0), 1);

  const cellColor = (count) => {
    if (!count) return '#e2e8f0';
    const ratio = count / maxCount;
    if (ratio < 0.25) return '#93c5fd';
    if (ratio < 0.5)  return '#60a5fa';
    if (ratio < 0.75) return '#3b82f6';
    return '#1d4ed8';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'nowrap', overflowX: 'auto', pb: 1 }}>
        {dates.map((date) => {
          const info = dailyActivity[date];
          return (
            <Tooltip
              key={date}
              title={`${formatDateLabel(date)}: ${info?.count || 0} events`}
              placement="top"
              arrow
            >
              <Box
                sx={{
                  width: 14, height: 14, borderRadius: 0.5,
                  bgcolor: cellColor(info?.count || 0),
                  cursor: 'default', flexShrink: 0,
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.disabled">
          {days} days ago
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.disabled">Less</Typography>
          {['#e2e8f0', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8'].map(c => (
            <Box key={c} sx={{ width: 10, height: 10, borderRadius: 0.3, bgcolor: c }} />
          ))}
          <Typography variant="caption" color="text.disabled">More</Typography>
        </Box>
        <Typography variant="caption" color="text.disabled">Today</Typography>
      </Box>
    </Box>
  );
}

// ── Event Row ──────────────────────────────────────────────────────────────────

function EventRow({ ev, onNavigateToHub }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const meta = EVENT_META[ev.type] || EVENT_META.hub_visit;
  const isQuery = ev.type === 'ai_query';
  const hasFullText = isQuery && ev.queryText?.length > 60;

  const handleReask = (e) => {
    e.stopPropagation();
    if (ev.hubId && onNavigateToHub) {
      onNavigateToHub(ev.hubId);
    } else if (ev.queryText) {
      navigator.clipboard.writeText(ev.queryText).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <Box>
      <Box
        onClick={() => hasFullText && setExpanded(x => !x)}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1,
          cursor: hasFullText ? 'pointer' : 'default',
          '&:hover': hasFullText ? { bgcolor: 'action.hover', borderRadius: 1 } : {},
          px: 1,
        }}
      >
        {/* Icon */}
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: meta.color + '18', color: meta.color, flexShrink: 0, mt: 0.1,
        }}>
          {meta.icon}
        </Box>

        {/* Description */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {eventDescription(ev)}
          </Typography>
          {ev.hubName && ev.type !== 'hub_visit' && (
            <Typography variant="caption" color="text.disabled">
              in {ev.hubName}
            </Typography>
          )}
        </Box>

        {/* Re-ask button for AI queries */}
        {isQuery && ev.queryText && (
          <Tooltip title={ev.hubId ? 'Open hub and re-ask' : 'Copy query to clipboard'}>
            <Chip
              icon={ev.hubId ? <OpenHubIcon sx={{ fontSize: '13px !important' }} /> : <CopyIcon sx={{ fontSize: '13px !important' }} />}
              label={copied ? 'Copied!' : 'Re-ask'}
              size="small"
              onClick={handleReask}
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 600,
                bgcolor: copied ? '#10b98115' : '#8b5cf615',
                color: copied ? '#10b981' : '#8b5cf6',
                cursor: 'pointer', flexShrink: 0,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Tooltip>
        )}

        {/* Time + expand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Typography variant="caption" color="text.disabled">
            {formatTime(ev.ts)}
          </Typography>
          {hasFullText && (
            expanded ? <ExpandLessIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                     : <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          )}
        </Box>
      </Box>

      {/* Expanded query text */}
      <Collapse in={expanded}>
        <Box sx={{ ml: 5.5, mb: 1, p: 1.5, bgcolor: '#f8f5ff', borderRadius: 1.5, borderLeft: '3px solid #8b5cf6' }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {ev.queryText}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function LearningJourneyView({ userId, user, onClose, onOpenPdfAtPage, onNavigateToHub }) {
  const [activeTab, setActiveTab] = useState(0);
  const [days, setDays] = useState(30);
  const [events, setEvents] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Analytics state
  const [bloomsCounts, setBloomsCounts] = useState(null);
  const [bloomsLoading, setBloomsLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // Study Plans state
  const [studyPlans, setStudyPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planInsights, setPlanInsights] = useState(null);
  const [planInsightsLoading, setPlanInsightsLoading] = useState(false);

  // Report dialog
  const [showReport, setShowReport] = useState(false);

  // Activity log filter
  const [logFilter, setLogFilter] = useState('all');
  const LOG_FILTERS = ['all', 'ai_query', 'hub_visit', 'pdf_open'];

  // ── Load data ────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [evs, sessions] = await Promise.all([
        getJourneyEvents(userId, days),
        getSessionHistory(userId, 100).catch(() => []),
      ]);
      setEvents(evs);
      setSessionData(sessions);
    } finally {
      setLoading(false);
    }
  }, [userId, days]);

  const loadStudyPlans = useCallback(async () => {
    if (!userId) return;
    setPlansLoading(true);
    try {
      const plans = await getAllStudyPlans(userId);
      setStudyPlans(plans);
    } finally {
      setPlansLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === 5) loadStudyPlans(); }, [activeTab, loadStudyPlans]);

  // ── Derived data ─────────────────────────────────────────────────────────────

  const dailyActivity = getDailyActivity(events);
  const topHubs = getTopHubs(events, 6);
  const hours = getHourDistribution(events);
  const aiQueryEvents = events.filter(e => e.type === 'ai_query');

  const totalStudyMs = sessionData.reduce((s, sess) => s + (sess.duration || 0), 0);
  const totalStudyHrs = (totalStudyMs / 3600000).toFixed(1);
  const streak = (() => {
    let s = 0;
    const today = new Date().toISOString().slice(0, 10);
    let check = today;
    while (dailyActivity[check]) {
      s++;
      const d = new Date(check + 'T12:00:00');
      d.setDate(d.getDate() - 1);
      check = d.toISOString().slice(0, 10);
    }
    return s;
  })();

  // Grouped by date for Activity Log
  const grouped = {};
  const filtered = logFilter === 'all' ? events : events.filter(e => e.type === logFilter);
  for (const ev of filtered) {
    const date = ev.date || ev.ts?.toISOString?.().slice(0, 10) || 'Unknown';
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(ev);
  }
  const groupedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  // Mode distribution for doughnut
  const modeCounts = {};
  for (const ev of aiQueryEvents) {
    const m = ev.mode || 'Custom';
    modeCounts[m] = (modeCounts[m] || 0) + 1;
  }

  // ── Bloom's ──────────────────────────────────────────────────────────────────

  const handleAnalyzeBlooms = async () => {
    setBloomsLoading(true);
    try {
      const counts = await analyzeBloomsLevel(userId, aiQueryEvents);
      setBloomsCounts(counts);
    } finally {
      setBloomsLoading(false);
    }
  };

  // ── Seed demo data (for previewing the feature) ─────────────────────────────

  const handleSeedDemo = async () => {
    if (!userId) return;
    const sampleHubs = ['Physics', 'Mathematics', 'History'];
    const sampleModes = ['Explain', 'Summarize', 'Solve', 'Analyze'];
    const sampleQueries = [
      'What is Newton\'s second law?',
      'Summarize chapter 3 on thermodynamics',
      'Solve this integral: ∫x²dx',
      'Explain the French Revolution causes',
      'What is the quadratic formula?',
    ];
    const now = Date.now();
    const promises = [];
    for (let i = 0; i < 12; i++) {
      const hub = sampleHubs[i % sampleHubs.length];
      const daysAgo = Math.floor(i / 3);
      const date = new Date(now - daysAgo * 86400000);
      // Temporarily shift clock via overriding log
      const eventDate = date.toISOString().slice(0, 10);
      promises.push(logJourneyEvent(userId, 'hub_visit', { hubName: hub, date: eventDate }));
      promises.push(logJourneyEvent(userId, 'ai_query', {
        hubName: hub,
        mode: sampleModes[i % sampleModes.length],
        queryText: sampleQueries[i % sampleQueries.length],
        date: eventDate,
      }));
    }
    await Promise.allSettled(promises);
    setTimeout(() => loadData(), 2000); // reload after Firestore write
  };

  // ── Insights ─────────────────────────────────────────────────────────────────

  const handleGenerateInsights = async () => {
    setInsightsLoading(true);
    try {
      const result = await generateInsights(events, bloomsCounts || {});
      setInsights(result);
    } finally {
      setInsightsLoading(false);
    }
  };

  // ── Stat card ─────────────────────────────────────────────────────────────────

  const StatCard = ({ label, value, icon, color }) => (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 120, p: 2, borderRadius: 2.5,
      border: '1px solid', borderColor: 'divider',
      display: 'flex', flexDirection: 'column', gap: 0.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color }}>
        {icon}
        <Typography variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.6}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
    </Paper>
  );

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{
      height: '100%',
      bgcolor: 'background.default', display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', flexShrink: 0,
      }}>
        <IconButton onClick={onClose} size="small">
          <BackIcon />
        </IconButton>
        <BrainIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          Learning Journey
        </Typography>

        {/* Range selector */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[7, 30, 90].map(d => (
            <Chip
              key={d}
              label={d === 90 ? '3m' : `${d}d`}
              size="small"
              variant={days === d ? 'filled' : 'outlined'}
              color={days === d ? 'primary' : 'default'}
              onClick={() => setDays(d)}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>

        <Button
          variant="outlined" size="small"
          startIcon={<ReportIcon />}
          onClick={() => setShowReport(true)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.8rem' }}
        >
          My Report
        </Button>

        <IconButton onClick={loadData} size="small" title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats strip */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <StatCard label="Study Time" value={`${totalStudyHrs}h`} icon={<TimeIcon sx={{ fontSize: 16 }} />} color="#3b82f6" />
        <StatCard label="AI Queries" value={aiQueryEvents.length} icon={<AIIcon sx={{ fontSize: 16 }} />} color="#8b5cf6" />
        <StatCard label="Hubs Visited" value={topHubs.length} icon={<HubIcon sx={{ fontSize: 16 }} />} color="#10b981" />
        <StatCard label="Streak" value={`🔥 ${streak}d`} icon={<FireIcon sx={{ fontSize: 16 }} />} color="#f59e0b" />
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, minHeight: 42 }}
        TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
      >
        <Tab label="Overview" sx={{ minHeight: 42, py: 0 }} />
        <Tab label="Activity Log" sx={{ minHeight: 42, py: 0 }} />
        <Tab label="Analytics" sx={{ minHeight: 42, py: 0 }} />
        <Tab label="Insights" sx={{ minHeight: 42, py: 0 }} />
        <Tab label="Quiz Results" sx={{ minHeight: 42, py: 0 }} />
        <Tab label="Study Plans" sx={{ minHeight: 42, py: 0, fontWeight: activeTab === 5 ? 700 : 400 }} />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={80} />)}
          </Box>
        ) : (
          <>
            {/* ── TAB 0: OVERVIEW ─────────────────────────────────────────── */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Heatmap */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                    Activity Heatmap — last {days} days
                  </Typography>
                  <ActivityHeatmap dailyActivity={dailyActivity} days={days} />
                </Paper>

                {/* Top Hubs */}
                {topHubs.length > 0 && (
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Top Hubs</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {topHubs.map(({ name, count }) => (
                        <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            flex: 1, height: 8, borderRadius: 1, bgcolor: '#e2e8f0', overflow: 'hidden',
                          }}>
                            <Box sx={{
                              height: '100%', bgcolor: 'primary.main', borderRadius: 1,
                              width: `${Math.min(100, (count / (topHubs[0]?.count || 1)) * 100)}%`,
                            }} />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 160, fontWeight: 500 }}>{name}</Typography>
                          <Chip label={count} size="small" variant="outlined" />
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}

                {/* Recent Activity */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Recent Activity</Typography>
                  {events.slice(0, 20).map(ev => (
                    <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                      <Box sx={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        bgcolor: (EVENT_META[ev.type]?.color || '#94a3b8') + '18',
                        color: EVENT_META[ev.type]?.color || '#94a3b8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {EVENT_META[ev.type]?.icon}
                      </Box>
                      <Typography variant="body2" sx={{ flex: 1 }}>{eventDescription(ev)}</Typography>
                      <Typography variant="caption" color="text.disabled">{formatRelativeTime(ev.ts)}</Typography>
                    </Box>
                  ))}
                  {events.length === 0 && !loading && (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.disabled" sx={{ mb: 1.5 }}>
                        No activity yet — open a Hub and ask questions to start tracking
                      </Typography>
                      <Button size="small" variant="outlined" onClick={handleSeedDemo}>
                        Add sample data to preview
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {/* ── TAB 1: ACTIVITY LOG ─────────────────────────────────────── */}
            {activeTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Filter */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[['all', 'All'], ['ai_query', 'AI Queries'], ['hub_visit', 'Hub Visits'], ['pdf_open', 'PDFs']].map(([val, label]) => (
                    <Chip
                      key={val}
                      label={label}
                      size="small"
                      variant={logFilter === val ? 'filled' : 'outlined'}
                      color={logFilter === val ? 'primary' : 'default'}
                      onClick={() => setLogFilter(val)}
                    />
                  ))}
                </Box>

                {/* Date-grouped list */}
                {groupedDates.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ py: 4, textAlign: 'center' }}>
                    No events in this range
                  </Typography>
                ) : groupedDates.map(date => (
                  <Box key={date}>
                    <Chip
                      label={formatDateLabel(date)}
                      size="small"
                      sx={{ mb: 0.5, fontWeight: 700, bgcolor: 'action.selected' }}
                    />
                    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      {grouped[date].map((ev, i) => (
                        <Box key={ev.id || i} sx={{ borderTop: i > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                          <EventRow ev={ev} onNavigateToHub={onNavigateToHub} />
                        </Box>
                      ))}
                    </Paper>
                  </Box>
                ))}
              </Box>
            )}

            {/* ── TAB 2: ANALYTICS ────────────────────────────────────────── */}
            {activeTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Bloom's Taxonomy */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>Bloom's Taxonomy</Typography>
                      <Typography variant="caption" color="text.secondary">Cognitive depth of your AI queries</Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={bloomsLoading ? <CircularProgress size={12} /> : <AnalyticsIcon sx={{ fontSize: 14 }} />}
                      onClick={handleAnalyzeBlooms}
                      disabled={bloomsLoading || aiQueryEvents.length === 0}
                    >
                      {bloomsLoading ? 'Analyzing…' : 'Analyze with AI'}
                    </Button>
                  </Box>

                  {bloomsCounts ? (
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ width: 220, height: 220 }}>
                        <Radar
                          data={{
                            labels: BLOOMS_LEVELS,
                            datasets: [{
                              label: 'Your Queries',
                              data: BLOOMS_LEVELS.map(l => bloomsCounts[l] || 0),
                              backgroundColor: 'rgba(139,92,246,0.15)',
                              borderColor: '#8b5cf6',
                              pointBackgroundColor: BLOOMS_LEVELS.map(l => BLOOMS_COLORS[l]),
                              pointRadius: 4,
                              borderWidth: 2,
                            }],
                          }}
                          options={{
                            responsive: true, maintainAspectRatio: true,
                            plugins: { legend: { display: false } },
                            scales: {
                              r: {
                                beginAtZero: true,
                                ticks: { stepSize: 1, font: { size: 10 } },
                                pointLabels: { font: { size: 11, weight: '600' } },
                              },
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {BLOOMS_LEVELS.map(l => (
                          <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: BLOOMS_COLORS[l] }} />
                            <Typography variant="body2" fontWeight={500} sx={{ minWidth: 90 }}>{l}</Typography>
                            <Chip label={bloomsCounts[l] || 0} size="small" variant="outlined" />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <AnalyticsIcon sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
                      <Typography variant="body2">
                        {aiQueryEvents.length === 0
                          ? 'No AI queries yet — use Hub Chat to start'
                          : 'Click "Analyze with AI" to classify your queries by cognitive depth'}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Query Mode Doughnut */}
                {Object.keys(modeCounts).length > 0 && (
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Query Mode Distribution</Typography>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ width: 180, height: 180 }}>
                        <Doughnut
                          data={{
                            labels: Object.keys(modeCounts),
                            datasets: [{
                              data: Object.values(modeCounts),
                              backgroundColor: [
                                '#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899',
                              ],
                              borderWidth: 2,
                              borderColor: '#fff',
                            }],
                          }}
                          options={{
                            responsive: true, maintainAspectRatio: true,
                            plugins: { legend: { position: 'right', labels: { font: { size: 11 }, padding: 8 } } },
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                )}

                {/* Daily Activity Bar */}
                {Object.keys(dailyActivity).length > 0 && (
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Daily Activity</Typography>
                    <Box sx={{ height: 160 }}>
                      <Bar
                        data={{
                          labels: Object.keys(dailyActivity).sort().map(d => {
                            const dt = new Date(d + 'T12:00:00');
                            return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                          }),
                          datasets: [{
                            label: 'Events',
                            data: Object.keys(dailyActivity).sort().map(d => dailyActivity[d]?.count || 0),
                            backgroundColor: 'rgba(59,130,246,0.6)',
                            borderColor: '#3b82f6',
                            borderWidth: 1,
                            borderRadius: 3,
                          }],
                        }}
                        options={{
                          responsive: true, maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: {
                            x: { ticks: { font: { size: 10 }, maxRotation: 45 } },
                            y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } },
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                )}

                {/* Study Hour Distribution */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Study Hour Distribution</Typography>
                  <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'flex-end', height: 48 }}>
                    {hours.map((count, h) => {
                      const max = Math.max(...hours, 1);
                      return (
                        <Tooltip key={h} title={`${h}:00 — ${count} events`} arrow placement="top">
                          <Box sx={{
                            flex: 1,
                            height: `${Math.max(4, (count / max) * 48)}px`,
                            bgcolor: count > 0 ? '#3b82f6' : '#e2e8f0',
                            borderRadius: '2px 2px 0 0',
                            opacity: count > 0 ? 0.6 + (count / max) * 0.4 : 1,
                            cursor: 'default',
                            transition: 'opacity 0.2s',
                          }} />
                        </Tooltip>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.disabled">12am</Typography>
                    <Typography variant="caption" color="text.disabled">6am</Typography>
                    <Typography variant="caption" color="text.disabled">12pm</Typography>
                    <Typography variant="caption" color="text.disabled">6pm</Typography>
                    <Typography variant="caption" color="text.disabled">11pm</Typography>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* ── TAB 4: QUIZ RESULTS ─────────────────────────────────────── */}
            {activeTab === 4 && (() => {
              const quizEvents = events.filter(e => e.type === 'quiz_complete');
              const BLOOMS_LEVELS = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'];
              const BLOOMS_COLORS = { Remember:'#64b5f6', Understand:'#81c784', Apply:'#ffb74d', Analyze:'#ba68c8', Evaluate:'#f06292', Create:'#4db6ac' };
              const DOK_COLORS = ['#42a5f5','#66bb6a','#ffa726','#ef5350'];

              if (quizEvents.length === 0) {
                return (
                  <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    <QuizResultsIcon sx={{ fontSize: 56, opacity: 0.15, mb: 1.5 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>No quiz results yet</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Complete a Practice Quiz in any hub to see your results here.
                    </Typography>
                  </Box>
                );
              }

              // Aggregate across all quizzes
              const totalQuizzes = quizEvents.length;
              const avgPct = Math.round(quizEvents.reduce((s, e) => s + (e.pct || 0), 0) / totalQuizzes);
              const totalCorrect = quizEvents.reduce((s, e) => s + (e.score || 0), 0);
              const totalQuestions = quizEvents.reduce((s, e) => s + (e.total || 0), 0);

              // Aggregate Bloom's across all quizzes
              const aggBlooms = {};
              BLOOMS_LEVELS.forEach(l => { aggBlooms[l] = { attempted: 0, correct: 0 }; });
              quizEvents.forEach(e => {
                if (e.byBlooms) {
                  BLOOMS_LEVELS.forEach(l => {
                    if (e.byBlooms[l]) {
                      aggBlooms[l].attempted += e.byBlooms[l].attempted || 0;
                      aggBlooms[l].correct   += e.byBlooms[l].correct   || 0;
                    }
                  });
                }
              });

              // Aggregate DOK
              const aggDOK = { 1:{attempted:0,correct:0}, 2:{attempted:0,correct:0}, 3:{attempted:0,correct:0}, 4:{attempted:0,correct:0} };
              quizEvents.forEach(e => {
                if (e.byDOK) {
                  [1,2,3,4].forEach(d => {
                    if (e.byDOK[d]) {
                      aggDOK[d].attempted += e.byDOK[d].attempted || 0;
                      aggDOK[d].correct   += e.byDOK[d].correct   || 0;
                    }
                  });
                }
              });

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Summary cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                    {[
                      { label: 'Quizzes Taken', value: totalQuizzes, icon: <QuizResultsIcon sx={{ color: '#6366f1', fontSize: 20 }} /> },
                      { label: 'Avg Score', value: `${avgPct}%`, icon: <TrophyIcon sx={{ color: '#fbbf24', fontSize: 20 }} /> },
                      { label: 'Total Questions', value: `${totalCorrect}/${totalQuestions}`, icon: <CorrectIcon sx={{ color: '#10b981', fontSize: 20 }} /> },
                    ].map(({ label, value, icon }) => (
                      <Paper key={label} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                        {icon}
                        <Typography variant="h5" fontWeight={800} mt={0.5}>{value}</Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  {/* Aggregate Bloom's Radar */}
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Cumulative Bloom's Taxonomy Performance</Typography>
                    <Box sx={{ height: 240 }}>
                      <Radar
                        data={{
                          labels: BLOOMS_LEVELS,
                          datasets: [
                            { label: 'Attempted', data: BLOOMS_LEVELS.map(l => aggBlooms[l]?.attempted || 0), backgroundColor: '#6366f125', borderColor: '#6366f1', borderWidth: 2, pointBackgroundColor: '#6366f1' },
                            { label: 'Correct',   data: BLOOMS_LEVELS.map(l => aggBlooms[l]?.correct   || 0), backgroundColor: '#10b98125', borderColor: '#10b981', borderWidth: 2, pointBackgroundColor: '#10b981' },
                          ],
                        }}
                        options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }, scales: { r: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } }, pointLabels: { font: { size: 10, weight: '600' } } } } }}
                      />
                    </Box>
                  </Paper>

                  {/* DOK Bar */}
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Depth of Knowledge (DOK) Performance</Typography>
                    <Box sx={{ height: 200 }}>
                      <Bar
                        data={{
                          labels: ['DOK 1 — Recall', 'DOK 2 — Skills', 'DOK 3 — Strategic', 'DOK 4 — Extended'],
                          datasets: [
                            { label: 'Attempted', data: [1,2,3,4].map(d => aggDOK[d]?.attempted || 0), backgroundColor: DOK_COLORS.map(c => c + '80'), borderColor: DOK_COLORS, borderWidth: 2 },
                            { label: 'Correct',   data: [1,2,3,4].map(d => aggDOK[d]?.correct   || 0), backgroundColor: '#10b98150', borderColor: '#10b981', borderWidth: 2 },
                          ],
                        }}
                        options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 10 } } }, y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } } } }}
                      />
                    </Box>
                  </Paper>

                  {/* Quiz history list */}
                  <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight={700}>Quiz History</Typography>
                    </Box>
                    {[...quizEvents].reverse().map((q, i) => {
                      const date = q.timestamp?.toDate ? q.timestamp.toDate() : new Date(q.date);
                      return (
                        <Box key={i} sx={{ px: 2.5, py: 2, borderBottom: i < quizEvents.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{q.pdfName || q.hubName || 'Quiz'}</Typography>
                              <Typography variant="caption" color="text.secondary">{date.toLocaleDateString()} · {q.difficulty || 'Mixed'} difficulty</Typography>
                            </Box>
                            <Chip
                              label={`${q.score ?? 0} / ${q.total ?? 0} (${q.pct ?? 0}%)`}
                              size="small"
                              color={(q.pct ?? 0) >= 70 ? 'success' : (q.pct ?? 0) >= 50 ? 'warning' : 'error'}
                              sx={{ fontWeight: 700 }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {BLOOMS_LEVELS.map(l => {
                              const bl = q.byBlooms?.[l];
                              if (!bl?.attempted) return null;
                              const pct = Math.round((bl.correct / bl.attempted) * 100);
                              return (
                                <Chip
                                  key={l}
                                  label={`${l} ${pct}%`}
                                  size="small"
                                  sx={{ bgcolor: BLOOMS_COLORS[l] + '30', color: BLOOMS_COLORS[l], fontSize: '0.65rem', height: 20, fontWeight: 600 }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Paper>
                </Box>
              );
            })()}

            {/* ── TAB 3: INSIGHTS ─────────────────────────────────────────── */}
            {activeTab === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>AI Learning Insights</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Personalized analysis of your last {days} days
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={insightsLoading ? <CircularProgress size={12} color="inherit" /> : <InsightIcon sx={{ fontSize: 14 }} />}
                      onClick={handleGenerateInsights}
                      disabled={insightsLoading || events.length === 0}
                    >
                      {insightsLoading ? 'Generating…' : insights ? 'Refresh' : 'Generate Insights'}
                    </Button>
                  </Box>

                  {insightsLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {[120, 80, 100, 60].map((w, i) => (
                        <Skeleton key={i} variant="rounded" height={w} />
                      ))}
                    </Box>
                  ) : insights ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      {/* Overall */}
                      {insights.overall && (
                        <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 2, borderLeft: '4px solid #3b82f6' }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{insights.overall}</Typography>
                        </Box>
                      )}

                      {/* Strengths */}
                      {insights.strengths?.length > 0 && (
                        <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, borderLeft: '4px solid #166534' }}>
                          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: '#166534', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            ✓ Strengths
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {insights.strengths.map((s, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#166534', mt: 0.7, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>{s}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Improvements */}
                      {insights.improvements?.length > 0 && (
                        <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 2, borderLeft: '4px solid #1d4ed8' }}>
                          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            → Areas to Improve
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {insights.improvements.map((s, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1d4ed8', mt: 0.7, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>{s}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Habits */}
                      {insights.habits?.length > 0 && (
                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, borderLeft: '4px solid #475569' }}>
                          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: '#334155', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            ◉ Study Habits
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {insights.habits.map((h, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#475569', mt: 0.7, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>{h}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <InsightIcon sx={{ fontSize: 48, opacity: 0.15, mb: 1 }} />
                      <Typography variant="body2">
                        {events.length === 0
                          ? 'No activity data yet — start learning to generate insights'
                          : 'Click "Generate Insights" to get personalized recommendations'}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}
            {/* ── TAB 5: STUDY PLANS ──────────────────────────────────────── */}
            {activeTab === 5 && (() => {
              // Helpers
              const totalSteps = (p) => (p.plan?.days || []).reduce((s, d) => s + (d.steps?.length || 0), 0);
              const pctDone = (p) => { const t = totalSteps(p); return t ? Math.round(((p.completedStepIds?.length || 0) / t) * 100) : 0; };
              const timeMs = (p) => Object.values(p.stepTimes || {}).reduce((s, v) => s + (v || 0), 0);
              const fmtMs = (ms) => { const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000); return h > 0 ? `${h}h ${m}m` : `${m}m`; };
              const quizPct = (p) => p.quizResults?.pct ?? null;
              const gradeColor = (g) => !g ? '#94a3b8' : g.startsWith('A') ? '#10b981' : g.startsWith('B') ? '#3b82f6' : g.startsWith('C') ? '#f59e0b' : '#ef4444';

              const completed = studyPlans.filter(p => p.phase === 'complete' || pctDone(p) === 100);
              const inProgress = studyPlans.filter(p => p.phase !== 'complete' && pctDone(p) < 100 && pctDone(p) > 0);
              const totalPlanTimeMs = studyPlans.reduce((s, p) => s + timeMs(p), 0);
              const scores = studyPlans.map(quizPct).filter(s => s !== null);
              const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

              // Subject breakdown
              const subjectMap = {};
              studyPlans.forEach(p => {
                const subj = p.plan?.subject || 'General';
                if (!subjectMap[subj]) subjectMap[subj] = 0;
                subjectMap[subj]++;
              });

              // Score trend (oldest → newest)
              const scoreTrend = [...studyPlans].reverse()
                .map(p => ({ label: (p.plan?.title || p.sourceName || 'Plan').slice(0, 14), score: quizPct(p) }))
                .filter(x => x.score !== null);

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {plansLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={72} />)}
                    </Box>
                  ) : studyPlans.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <PlanIcon sx={{ fontSize: 56, opacity: 0.12, mb: 1.5 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>No study plans yet</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Open a hub or PDF and tap the Study Plan tab to create your first personalised plan.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Summary stats */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.25 }}>
                        {[
                          { label: 'Total Plans',   value: studyPlans.length, color: '#6366f1' },
                          { label: 'Completed',     value: completed.length,  color: '#10b981' },
                          { label: 'Avg Quiz Score', value: avgScore !== null ? `${avgScore}%` : '—', color: '#ef4444' },
                          { label: 'Study Time',    value: fmtMs(totalPlanTimeMs), color: '#3b82f6' },
                        ].map(({ label, value, color }) => (
                          <Paper key={label} elevation={0} sx={{ p: 1.75, borderRadius: 2, border: '1px solid', borderColor: `${color}25`, bgcolor: `${color}08`, textAlign: 'center' }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color }}>{value}</Typography>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                          </Paper>
                        ))}
                      </Box>

                      {/* Per-plan cards */}
                      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>All Study Plans</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {studyPlans.map(p => {
                            const pct = pctDone(p);
                            const isDone = p.phase === 'complete' || pct === 100;
                            const grade = p.analytics?.grade;
                            const score = quizPct(p);
                            const ms = timeMs(p);
                            return (
                              <Box key={p.id} sx={{
                                p: 1.5, borderRadius: 1.75, border: '1px solid', borderColor: 'divider',
                                display: 'flex', gap: 1.25, alignItems: 'center',
                              }}>
                                {isDone
                                  ? <CompletedPlanIcon sx={{ color: '#10b981', fontSize: 18, flexShrink: 0 }} />
                                  : <InProgressPlanIcon sx={{ color: '#f59e0b', fontSize: 18, flexShrink: 0 }} />}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600} noWrap>
                                    {p.plan?.title || p.sourceName || 'Untitled Plan'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                                      <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 2, bgcolor: isDone ? '#10b981' : '#6366f1', transition: 'width 0.4s ease' }} />
                                    </Box>
                                    <Typography variant="caption" fontWeight={700} sx={{ minWidth: 32, color: isDone ? '#10b981' : 'text.secondary' }}>{pct}%</Typography>
                                  </Box>
                                  {p.plan?.subject && (
                                    <Typography variant="caption" color="text.disabled">{p.plan.subject}</Typography>
                                  )}
                                </Box>
                                {grade && (
                                  <Chip label={grade} size="small" sx={{ fontWeight: 800, height: 20, fontSize: '0.7rem', bgcolor: `${gradeColor(grade)}18`, color: gradeColor(grade) }} />
                                )}
                                {score !== null && (
                                  <Typography variant="caption" fontWeight={700} sx={{ color: '#ef4444', minWidth: 32, textAlign: 'right' }}>{score}%</Typography>
                                )}
                                {ms > 0 && (
                                  <Typography variant="caption" color="text.disabled" sx={{ minWidth: 38, textAlign: 'right', fontSize: '0.7rem' }}>{fmtMs(ms)}</Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>

                      {/* Score trend chart */}
                      {scoreTrend.length >= 2 && (
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Quiz Score Trend</Typography>
                          <Box sx={{ height: 180 }}>
                            <Bar
                              data={{
                                labels: scoreTrend.map(x => x.label),
                                datasets: [{
                                  label: 'Quiz %',
                                  data: scoreTrend.map(x => x.score),
                                  backgroundColor: scoreTrend.map(x => x.score >= 80 ? '#10b98180' : x.score >= 60 ? '#6366f180' : '#ef444480'),
                                  borderRadius: 5,
                                }],
                              }}
                              options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { min: 0, max: 100, ticks: { stepSize: 20 } } } }}
                            />
                          </Box>
                        </Paper>
                      )}

                      {/* Subject breakdown */}
                      {Object.keys(subjectMap).length > 0 && (
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight={700} mb={1.25}>Subjects Studied</Typography>
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {Object.entries(subjectMap).map(([subj, cnt]) => (
                              <Chip key={subj} label={`${subj} × ${cnt}`} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                            ))}
                          </Box>
                        </Paper>
                      )}

                      {/* AI insights for study plans */}
                      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight={700}>AI Study Plan Insights</Typography>
                          {!planInsights && !planInsightsLoading && (
                            <Button size="small" variant="outlined" startIcon={<AIIcon />}
                              onClick={async () => {
                                setPlanInsightsLoading(true);
                                try {
                                  const planEvents = studyPlans.map(p => ({
                                    type: 'study_plan',
                                    hubName: p.sourceName || p.plan?.title,
                                    queryText: `Plan: ${p.plan?.title}, Grade: ${p.analytics?.grade || 'N/A'}, Score: ${quizPct(p) ?? 'N/A'}%, Completion: ${pctDone(p)}%`,
                                    mode: 'study_plan',
                                  }));
                                  const result = await generateInsights([...events, ...planEvents], bloomsCounts || {});
                                  setPlanInsights(result);
                                } catch { /* silent */ } finally {
                                  setPlanInsightsLoading(false);
                                }
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                              Generate Insights
                            </Button>
                          )}
                        </Box>
                        {planInsightsLoading ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">Analysing your study plan data…</Typography>
                          </Box>
                        ) : planInsights ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {planInsights.overall && <Box sx={{ p: 1.5, bgcolor: '#f0f9ff', borderRadius: 1.5, borderLeft: '3px solid #3b82f6' }}><Typography variant="body2" sx={{ lineHeight: 1.7 }}>{planInsights.overall}</Typography></Box>}
                            {planInsights.strengths?.length > 0 && (
                              <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1.5, borderLeft: '3px solid #10b981' }}>
                                <Typography variant="caption" fontWeight={700} color="#166534" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>Strengths</Typography>
                                {planInsights.strengths.map((s, i) => <Typography key={i} variant="body2" sx={{ lineHeight: 1.65 }}>• {s}</Typography>)}
                              </Box>
                            )}
                            {planInsights.improvements?.length > 0 && (
                              <Box sx={{ p: 1.5, bgcolor: '#eff6ff', borderRadius: 1.5, borderLeft: '3px solid #3b82f6' }}>
                                <Typography variant="caption" fontWeight={700} color="#1d4ed8" sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>Areas to Improve</Typography>
                                {planInsights.improvements.map((s, i) => <Typography key={i} variant="body2" sx={{ lineHeight: 1.65 }}>→ {s}</Typography>)}
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
                            Click "Generate Insights" for AI-powered recommendations based on your study plan history.
                          </Typography>
                        )}
                      </Paper>
                    </>
                  )}
                </Box>
              );
            })()}
          </>
        )}
      </Box>

      {/* ── Student Report Dialog ── */}
      {showReport && (
        <StudentReportView
          open={showReport}
          onClose={() => setShowReport(false)}
          userId={userId}
          user={user}
          events={events}
          studyPlans={studyPlans}
        />
      )}
    </Box>
  );
}
